import { escapeXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ VENDOR BANK FILE DETAIL CONFIGURATION ============
const VENDOR_BANK_DETAIL_CONFIG = {
  parentObject: 'VENDOR',
  parentKeyField: 'VENDORID',
  containerTag: 'VENDORBANKFILEDETAILS',
  entryTag: 'vendorbankfiledetail',
  parentOnlyFields: ['PYMTCOUNTRYCODE', 'FILEPAYMENTSERVICE'],
  includeKeyInEntry: true,
  suggestedHeaders: ['VENDORID', 'PYMTCOUNTRYCODE', 'FILEPAYMENTSERVICE', 'ACCOUNTTYPE', 'BANKACCOUNTNUMBER', 'ACCOUNTNAME', 'BUSINESSIDCODE', 'SECCODE'],
};
const VENDOR_BANK_EXCLUDED_FIELDS = ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY'];

const shouldExcludeFromVendorBankEntry = (key) => {
  const upperKey = key.toUpperCase();
  if (VENDOR_BANK_EXCLUDED_FIELDS.includes(upperKey)) return true;
  if (VENDOR_BANK_DETAIL_CONFIG.parentOnlyFields.includes(upperKey)) return true;
  return false;
};

// ============ DETECTION ============
export const isVendorBankDetailType = (objectName) => {
  return objectName?.toUpperCase() === 'VENDORBANKFILEDETAIL';
};

export const getVendorBankSuggestedHeaders = () => {
  return [...VENDOR_BANK_DETAIL_CONFIG.suggestedHeaders];
};

// ============ FUNCTIONS ============
export const getVendorBankCreateSummary = (records) => {
  const cfg = VENDOR_BANK_DETAIL_CONFIG;
  const grouped = {};
  const missingId = [];
  
  records.forEach((rec, idx) => {
    const vendorId = rec[cfg.parentKeyField];
    if (!vendorId) { missingId.push(idx + 2); return; }
    if (!grouped[vendorId]) grouped[vendorId] = { entries: 0, pymtCountry: null, fileService: null };
    grouped[vendorId].entries++;
    if (rec.PYMTCOUNTRYCODE) grouped[vendorId].pymtCountry = rec.PYMTCOUNTRYCODE;
    if (rec.FILEPAYMENTSERVICE) grouped[vendorId].fileService = rec.FILEPAYMENTSERVICE;
  });
  
  return {
    config: cfg,
    vendors: Object.entries(grouped).map(([id, info]) => ({ id, entryCount: info.entries, pymtCountry: info.pymtCountry, fileService: info.fileService })),
    missingIdRows: missingId,
    totalEntries: records.length,
    totalVendors: Object.keys(grouped).length
  };
};

export const buildVendorBankPreviewXml = (records) => {
  const cfg = VENDOR_BANK_DETAIL_CONFIG;
  
  const grouped = {};
  records.forEach(rec => {
    const id = rec[cfg.parentKeyField];
    if (!id) return;
    if (!grouped[id]) grouped[id] = [];
    grouped[id].push(rec);
  });
  
  const vendorIds = Object.keys(grouped);
  if (vendorIds.length === 0) return '<!-- No valid records with VENDORID -->';
  
  const firstId = vendorIds[0];
  const firstGroupRecords = grouped[firstId];
  
  const parentFields = { VENDORID: firstId };
  cfg.parentOnlyFields.forEach(field => {
    const val = firstGroupRecords[0]?.[field];
    if (val !== undefined && val !== '' && val !== null) parentFields[field] = val;
  });
  const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('\n      ');
  
  const sampleEntries = firstGroupRecords.slice(0, 2);
  const entriesXml = sampleEntries.map(entry => {
    const entryFields = Object.entries(entry)
      .filter(([k, v]) => !shouldExcludeFromVendorBankEntry(k) && v !== '' && v !== undefined && v !== null)
      .map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`)
      .join('\n          ');
    return `<${cfg.entryTag}>\n          ${entryFields}\n        </${cfg.entryTag}>`;
  }).join('\n        ');
  
  const moreEntriesNote = firstGroupRecords.length > 2 ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more entries ... -->` : '';
  const moreVendorsNote = vendorIds.length > 1 ? `\n\n<!-- ... and ${vendorIds.length - 1} more vendor(s) ... -->` : '';
  
  return `<function controlid="${escapeXml(firstId)}">
  <update>
    <${cfg.parentObject}>
      ${parentXml}
      <${cfg.containerTag}>
        ${entriesXml}${moreEntriesNote}
      </${cfg.containerTag}>
    </${cfg.parentObject}>
  </update>
</function>${moreVendorsNote}`;
};

export const writeVendorBankDetails = async (config, records) => {
  const cfg = VENDOR_BANK_DETAIL_CONFIG;
  
  const missingId = records.filter(r => !r[cfg.parentKeyField]);
  if (missingId.length > 0) throw new Error(`${missingId.length} record(s) missing ${cfg.parentKeyField}.`);
  
  const grouped = {};
  records.forEach(rec => {
    const id = rec[cfg.parentKeyField];
    if (!grouped[id]) grouped[id] = [];
    grouped[id].push(rec);
  });
  
  const functions = Object.entries(grouped).map(([vendorId, entries]) => {
    const parentFields = { VENDORID: vendorId };
    cfg.parentOnlyFields.forEach(field => {
      const val = entries[0][field];
      if (val !== undefined && val !== '' && val !== null) parentFields[field] = val;
    });
    const parentXml = Object.entries(parentFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
    
    const entriesXml = entries.map(entry => {
      const entryFields = {};
      Object.entries(entry).forEach(([key, val]) => {
        if (shouldExcludeFromVendorBankEntry(key)) return;
        if (val === '' || val === undefined || val === null) return;
        entryFields[key] = val;
      });
      const fieldsXml = Object.entries(entryFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
      return `<${cfg.entryTag}>${fieldsXml}</${cfg.entryTag}>`;
    }).join('');
    
    return `<function controlid="${escapeXml(String(vendorId))}"><update><${cfg.parentObject}>${parentXml}<${cfg.containerTag}>${entriesXml}</${cfg.containerTag}></${cfg.parentObject}></update></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, cfg.parentObject, xmlPayload, 'VENDOR BANK DETAIL UPDATE');
  return result.results;
};
